import React, { useState, useEffect } from "react";
import { GrAddCircle } from "react-icons/gr";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBinLine } from "react-icons/ri";
import config from "../../../config";

const OrganizationConfig = () => {
  const [selectedOrganization, setSelectedOrganization] = useState("Org A");
  const [districts, setDistricts] = useState([]);
  const [states, setStates] = useState([]);
  const [bankNames, setBankNames] = useState([]);
  const [organizationData, setOrganizationData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async (endpoint, setter) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch from ${endpoint}`);
      }

      const data = await response.json();
      setter(data);
    } catch (err) {
      setError(err.message);
      console.error(`Error fetching from ${endpoint}:`, err);
    }
  };

  useEffect(() => {
    fetchData(config.DISTRICT_ENDPOINT, setDistricts);
    fetchData(config.STATE_ENDPOINT, setStates);
    fetchData(config.BANKNAME_ENDPOINT, setBankNames);
  }, []);

  useEffect(() => {
    if (districts.length && states.length && bankNames.length) {
      const newOrgData = {
        "Org A": {
          district: [...districts],
          state: [...states],
          bankName: [...bankNames],
        },
        "Org B": {
          district: [...districts],
          state: [...states],
          bankName: [...bankNames],
        },
      };
      setOrganizationData(newOrgData);
    }
  }, [districts, states, bankNames]);

  const handleAdd = async (section) => {
    const newItem = prompt(`Add a new item to ${section}`);
    if (!newItem) return;

    try {
      const response = await fetch(`${config.API_BASE_URL}/${section}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newItem }),
      });

      if (!response.ok) throw new Error("Failed to add item");

      // Refetch data after successful add
      fetchData(config[`${section.toUpperCase()}_ENDPOINT`], (data) => {
        setOrganizationData((prev) => ({
          ...prev,
          [selectedOrganization]: {
            ...prev[selectedOrganization],
            [section]: data,
          },
        }));
      });
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEdit = async (section, index) => {
    const currentItem = organizationData[selectedOrganization][section][index];
    const updatedName = prompt("Edit the item:", currentItem.name);
    if (!updatedName || !currentItem?.id) return;

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/${section}/${currentItem.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ name: updatedName }),
        }
      );

      if (!response.ok) throw new Error("Failed to update item");

      // Refetch data after successful update
      fetchData(config[`${section.toUpperCase()}_ENDPOINT`], (data) => {
        setOrganizationData((prev) => ({
          ...prev,
          [selectedOrganization]: {
            ...prev[selectedOrganization],
            [section]: data,
          },
        }));
      });
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (section, index) => {
    const item = organizationData[selectedOrganization][section][index];
    if (
      !window.confirm("Are you sure you want to delete this item?") ||
      !item?.id
    )
      return;

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/${section}/${item.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (!response.ok) throw new Error("Failed to delete item");

      // Refetch data after successful delete
      fetchData(config[`${section.toUpperCase()}_ENDPOINT`], (data) => {
        setOrganizationData((prev) => ({
          ...prev,
          [selectedOrganization]: {
            ...prev[selectedOrganization],
            [section]: data,
          },
        }));
      });
    } catch (err) {
      alert(err.message);
    }
  };

  const renderSection = (title, sectionKey) => {
    const sectionItems =
      organizationData[selectedOrganization]?.[sectionKey] || [];

    return (
      <div className='grid-item'>
        <h2 className='section-title'>
          {title}
          <button
            className='add-config-button'
            onClick={() => handleAdd(sectionKey)}
          >
            <GrAddCircle />
            <span className='add-config-text'>Add New {title}</span>
          </button>
        </h2>
        <div className='config-table-wrapper'>
          <table className='config-table'>
            <thead>
              <tr>
                <th>{title}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sectionItems.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{item.name}</td>
                  <td className='actions'>
                    <button onClick={() => handleEdit(sectionKey, index)}>
                      <CiEdit />
                    </button>
                    <button onClick={() => handleDelete(sectionKey, index)}>
                      <RiDeleteBinLine />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      {Object.keys(organizationData).length > 0 ? (
        <div className='organization-block'>
          <div className='grid-container'>
            {renderSection("District", "district")}
            {renderSection("State", "state")}
            {renderSection("Bank Name", "bankName")}
          </div>
        </div>
      ) : (
        <p>Loading organization configuration...</p>
      )}
    </div>
  );
};

export default OrganizationConfig;
